import * as React from "react";
import styled from "styled-components";

import { FormContext } from "./context/FormContextProvider";

import { Form as RegistrationForm } from "./ui/Form";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { Select } from "./ui/Select";

import { Row } from "./ui/Row";
import { InputError } from "./ui/InputError";
import { TimesWrapper } from "./ui/TimesWrapper";
import { Textarea } from "./ui/Textarea";
import { Loader } from "./ui/Loader";
import { useParams } from "react-router";
import { Consents } from "./ui/Consents";
import { Heading } from "../../Heading";
import { ButtonGroup } from "./ui/ButtonGroup";
interface IRegistrationForm {
  data: any;
  consentText?: string;
  nybygg?: boolean;
  onSubmit?: () => void;
}

export const Form: React.FC<IRegistrationForm> = React.memo(
  ({ data, consentText, nybygg, onSubmit }) => {
    const params = useParams<{ sguid: string }>();
    const {
      error,
      showForm,
      interval,
      showCount,
      statusCheck,
      networkError,
      fetchingShowing,
      posting,
      success,
      failed,
      showings,
      fetchingShowings,
      selectedShowing,
      setSelectedShowing,
      available,
      selectedTime,
      setSelectedTime,
      selectedTimeError,
      participants,
      setParticipants,
      participantItems,
      participantError,
      firstName,
      setFirstName,
      firstNameError,
      lastName,
      setLastName,
      lastNameError,
      email,
      zip,
      zipError,
      setZip,
      setEmail,
      emailError,
      phone,
      setPhone,
      phoneError,
      comment,
      setComment,
      charactersLeft,
      commentError,
      showPrinted,
      handleSubmit,
      printedConsentError,
    } = React.useContext(FormContext);

    const isNybygg = data?.getProperty?.assignmentType === "NYBYGG";

    if (statusCheck) {
      return null;
    } else if (networkError) {
      return (
        <ShowingRegistrationWrapper className="visningspaamelding-form">
          <h1>Tjenesten er ikke tilgjengelig</h1>
          <p>Forsøk igjen om litt.</p>
        </ShowingRegistrationWrapper>
      );
    } else if (!showForm && !statusCheck) {
      return (
        <ShowingRegistrationWrapper className="visningspaamelding-form">
          <p>
            Påmelding til visning på denne eiendommen er dessverre ikke mulig.{" "}
            <a href={`https://privatmegleren.no/${params.sguid}`}>
              Ta kontakt med megler
            </a>{" "}
            for mer informasjon.
          </p>
        </ShowingRegistrationWrapper>
      );
    } else if (posting) {
      return (
        <ShowingRegistrationWrapper
          style={{ paddingTop: "80px" }}
          className="visningspaamelding-form"
        >
          <Loader text="Sender skjema, vennligst vent.." />
        </ShowingRegistrationWrapper>
      );
    } else {
      return (
        <ShowingRegistrationWrapper className="visningspaamelding-form">
          {success ? (
            <FormSuccessMessage isNybygg={isNybygg ?? false} data={data} />
          ) : (
            <>
              {data?.getProperty?.checklistpoints?.info?.event ? (
                <Heading tag="h1" center={true} className="title">
                  Meld deg på salgsmøte/event
                </Heading>
              ) : data?.getProperty?.id === "304249032" ? (
                <Heading tag="h1" center={true} className="title">
                  Book møte med megler
                </Heading>
              ) : data?.getProperty?.id === "304259042" ? (
                <Heading tag="h1" center={true} className="title">
                  Book telefonsamtale 
                </Heading>
              ) : (
                <Heading tag="h1" center={true} className="title">
                  Meld deg på visning
                </Heading>
              )}
              <p className="ingress">{data?.getProperty?.address}</p>
              {error ? <InputError>{error}</InputError> : null}
              {failed ? <InputError>{failed}</InputError> : null}
              <Select
                nybygg={nybygg}
                name="showing"
                placeholder={
                  fetchingShowings ? "Henter visninger..." : "Velg tidspunkt"
                }
                loading={fetchingShowings}
                selected={selectedShowing?.label}
                items={showings}
                onSelect={(selected) => {
                  setSelectedShowing(selected);
                }}
              />
              {interval &&
              selectedShowing?.id &&
              selectedShowing?.id !== "0" ? (
                <>
                  <TimesWrapper
                    timesError={selectedTimeError}
                    available={available}
                    loading={fetchingShowing}
                    selected={selectedTime}
                    showCount={showCount}
                    onSelect={(item) => {
                      setSelectedTime(item.from);
                    }}
                  />
                </>
              ) : null}
              {selectedShowing?.id && showPrinted ? (
                <>
                  <Consents
                    data={data}
                    showPrinted
                    printedConsentError={printedConsentError}
                  />
                </>
              ) : null}
              <RegistrationForm
                noValidate
                onSubmit={(e) => {
                  if (typeof onSubmit === "function") {
                    onSubmit();
                  }

                  handleSubmit(e);
                }}
                className={
                  (!interval && selectedShowing?.id) ||
                  (interval && selectedTime) ||
                  (!selectedTime && selectedShowing?.id === "0")
                    ? ""
                    : "disabled-registration-form"
                }
              >
                <Row style={{ marginTop: "20px" }}>
                  <Select
                    nybygg={nybygg}
                    label="Antall personer"
                    name="participants"
                    placeholder="Velg antall personer"
                    selected={participants?.label}
                    errorMessage={participantError}
                    items={participantItems}
                    onSelect={(selected) => {
                      setParticipants(selected);
                    }}
                  />
                </Row>

                <Row>
                  <Input
                    nybygg={nybygg}
                    type="text"
                    name="fornavn"
                    label="Fornavn *"
                    error={firstNameError ? true : false}
                    errorMessage={firstNameError}
                    placeholder="Eks. Ola"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Row>
                <Row>
                  <Input
                    nybygg={nybygg}
                    type="text"
                    name="etternavn"
                    label="Etternavn *"
                    error={lastNameError ? true : false}
                    errorMessage={lastNameError}
                    placeholder="Eks. Nordmann"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Row>
                <Row>
                  <Input
                    nybygg={nybygg}
                    type="email"
                    name="epost"
                    label="E-post *"
                    error={emailError ? true : false}
                    errorMessage={emailError}
                    placeholder="Eks. olanordmann@epost.no"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Row>
                <Row>
                  <Input
                    nybygg={nybygg}
                    type="tel"
                    name="mobil"
                    label="Mobil *"
                    error={phoneError ? true : false}
                    errorMessage={phoneError}
                    placeholder="Eks. 00000000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Row>
                <Row>
                  <Input
                    type="text"
                    name="zip"
                    label="Postnummer *"
                    error={zipError ? true : false}
                    placeholder="Eks. 0000"
                    errorMessage={zipError}
                    value={zip}
                    onChange={(e: any) => setZip(e.target.value)}
                  />
                </Row>
                <Row>
                  <Textarea
                    nybygg={nybygg}
                    label="Kommentar"
                    placeholder="Kommentar"
                    value={comment}
                    error={commentError ? true : false}
                    errorMessage={commentError}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <p
                    style={{
                      fontSize: "12px",
                      marginTop: "5px",
                      textAlign: "right",
                    }}
                  >
                    Antall tegn igjen: {charactersLeft}
                  </p>
                </Row>
                <Consents data={data} showValuation marketing />
                {consentText ? (
                  <p dangerouslySetInnerHTML={{ __html: consentText || "" }} />
                ) : (
                  <p style={{ fontSize: "14px" }}>
                    *Jeg ønsker salgsoppgaven tilsendt og at megler holder meg
                    oppdatert via e-post, sms eller telefon i forbindelse med
                    salget av denne eiendommen.
                    <br />
                    <br />
                    Dersom du ønsker å melde deg på visning uten å fylle ut
                    skjemaet over, kan du ringe megler direkte for å avtale tid{" "}
                    <a href={`https://privatmegleren.no/${params.sguid}`}>
                      her
                    </a>
                    .
                  </p>
                )}

                <Row className="center-button">
                  <Button type="primary" colorTheme="gold" disabled={posting}>
                    Meld meg på visningen
                  </Button>
                </Row>
              </RegistrationForm>
              <Row style={{ textAlign: "center" }}>
                <a
                  href="https://privatmegleren.no/personvern"
                  style={{ fontSize: "14px" }}
                  target="_blank"
                >
                  Personvernpolicy
                </a>
              </Row>
            </>
          )}
        </ShowingRegistrationWrapper>
      );
    }
  }
);

const ShowingRegistrationWrapper = styled.div`
  width: 400px;
  padding: 60px 20px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    &.nybygg {
      color: rgb(0, 0, 0);
    }
  }

  @media all and (max-width: 800px) {
    width: 100%;
    padding: 40px 20px 100px 20px;
  }
`;

const StyledFinancingSection = styled.div`
  margin-top: 2em;
`;

const FinancingSection: React.FC<{ overXMill: boolean; isNybygg: boolean }> = ({
  overXMill,
  isNybygg,
}) => {
  return (
    <StyledFinancingSection>
      <h2>Trenger du finansiering?</h2>
      {overXMill ? (
        <>
          <p>En rådgiver i Nordea tar gjerne kontakt med deg.</p>
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              onClick={() => {
                window.open(
                  isNybygg
                    ? "https://www.nordea.no/privat/vare-produkter/lan-og-kreditt/boliglan/jeg-onsker-a-kjope-en-bolig.html?cid=partner-in4wsy3hqf2gor7qcltm"
                    : "https://www.nordea.no/privat/vare-produkter/lan-og-kreditt/boliglan/privatmegleren-og-nordea-kontaktskjema.html?cid=partner-vmkjuzsl62",
                  "_blank"
                );
              }}
            >
              Ja, takk
            </Button>
          </ButtonGroup>
        </>
      ) : (
        <>
          <p>Finn ut hva Nordea kan tilby deg </p>
          <ButtonGroup>
            <Button
              type="primary"
              colorTheme="gold"
              onClick={() => {
                window.open(
                  isNybygg
                    ? "https://www.nordea.no/privat/vare-produkter/lan-og-kreditt/boliglan/jeg-onsker-a-kjope-en-bolig.html?cid=partner-in4wsy3hqf2gor7qcltm"
                    : "https://www.nordea.no/privat/vare-produkter/lan-og-kreditt/boliglan/jeg-onsker-a-kjope-en-bolig.html?cid=partner-zjtf13xw9z",
                  "_blank"
                );
              }}
            >
              Klikk her
            </Button>
          </ButtonGroup>
        </>
      )}
    </StyledFinancingSection>
  );
};

const StyledFormSuccessMessage = styled.div`
  padding: 20px 0;

  h1 {
    margin-bottom: 0.2em;
  }
`;

const FormSuccessMessage: React.FC<{ data: any; isNybygg: boolean }> = ({
  data,
  isNybygg,
}) => {
  return (
    <StyledFormSuccessMessage>
      <Heading tag="h1" center={true} className="title">
        Velkommen til visning av
      </Heading>
      <p style={{ marginTop: "0", fontSize: "23px" }}>
        {data?.getProperty?.address}
      </p>
      <p>
        Vi sender deg en bekreftelse på e-post. <br />
        Vi sees!
      </p>
      <FinancingSection
        isNybygg={isNybygg}
        overXMill={
          data?.getProperty?.price?.info?.price &&
          data?.getProperty?.price?.info?.price >= 5000000
        }
      />
    </StyledFormSuccessMessage>
  );
};
